import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { trackPurchase } from "@/lib/analytics";

// Reads the ?checkout_status and ?session_id URL params returned by Stripe
// Checkout, polls the backend until the payment settles, then shows a small
// confirmation / cancel banner. Clears the cart on success.
export default function StripeReturn() {
  const { clear } = useCart();
  const [state, setState] = useState({ kind: "idle", session_id: "" });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cs = params.get("checkout_status");
    const sid = params.get("session_id");
    if (!cs) return;

    // Strip the query params so refresh doesn't re-trigger
    const cleanUrl = window.location.pathname + window.location.hash;
    window.history.replaceState({}, "", cleanUrl);

    if (cs === "cancel") {
      setState({ kind: "cancelled", session_id: sid || "" });
      window.sessionStorage.removeItem("pending_stripe_session");
      return;
    }
    if (cs === "success" && sid) {
      setState({ kind: "checking", session_id: sid });
      // Eagerly clear the cart on redirect from Stripe success_url — the payment IS
      // authorised at this point (Stripe never redirects to success_url otherwise).
      // If polling later shows it as failed / expired, the customer will be told
      // clearly and can re-add. This prevents duplicate payments where a slow
      // Klarna approval left items in the cart and the customer tried again.
      clear();
      window.sessionStorage.removeItem("pending_stripe_session");
      let cancelled = false;
      let attempts = 0;
      // Klarna's async approval can take 30-60s. Poll for up to ~90s (30 × 3s).
      const maxAttempts = 30;
      const poll = async () => {
        if (cancelled) return;
        attempts += 1;
        try {
          const { data } = await api.get(`/checkout/status/${sid}`);
          if (data.payment_status === "paid") {
            trackPurchase({
              transaction_id: data.order_id || sid,
              amount_gbp: Number(data.amount_total || 0) / 100,
              currency: (data.currency || "gbp").toUpperCase(),
            });
            setState({ kind: "paid", session_id: sid, order_id: data.order_id });
            return;
          }
          if (data.status === "expired" || data.status === "canceled") {
            setState({ kind: "failed", session_id: sid });
            return;
          }
        } catch (e) {
          console.error("Poll error:", e);
        }
        if (attempts < maxAttempts) {
          setTimeout(poll, 3000);
        } else {
          setState({ kind: "pending", session_id: sid });
        }
      };
      poll();
      return () => { cancelled = true; };
    }
  }, [clear]);

  if (state.kind === "idle") return null;

  const dismiss = () => setState({ kind: "idle", session_id: "" });

  const box = (color, icon, title, body) => (
    <div
      className={`fixed z-[90] top-24 left-1/2 -translate-x-1/2 max-w-sm w-[92vw] border ${color} bg-[#0A0A0A] p-4 shadow-2xl`}
      role="alert"
      data-testid={`stripe-return-${state.kind}`}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5">{icon}</div>
        <div className="flex-1 min-w-0">
          <div className="font-serif text-lg text-white">{title}</div>
          <div className="text-xs text-white/60 mt-1">{body}</div>
        </div>
        <button onClick={dismiss} className="text-white/50 hover:text-white text-xs" aria-label="Dismiss">✕</button>
      </div>
    </div>
  );

  if (state.kind === "checking") {
    return box("border-[#D4AF37]/40",
      <Loader2 className="w-5 h-5 text-[#D4AF37] animate-spin" />,
      "Confirming your payment…",
      "Payment received — we're just finalising your order. This can take up to a minute for Klarna approvals. Please don't close this page.");
  }
  if (state.kind === "paid") {
    return box("border-emerald-500/40",
      <CheckCircle2 className="w-5 h-5 text-emerald-400" />,
      "Payment received — thank you!",
      state.order_id ? `Order #${state.order_id.slice(0,8).toUpperCase()} confirmed. A confirmation email is on its way and we'll be in touch shortly.` : "Your order is confirmed. A confirmation email is on its way and we'll be in touch shortly.");
  }
  if (state.kind === "cancelled") {
    return box("border-white/20",
      <XCircle className="w-5 h-5 text-white/60" />,
      "Checkout cancelled",
      "No worries — your bag is still saved and no card was charged.");
  }
  if (state.kind === "failed") {
    return box("border-red-500/40",
      <XCircle className="w-5 h-5 text-red-400" />,
      "Payment did not complete",
      "Please try again or contact us via WhatsApp.");
  }
  if (state.kind === "pending") {
    return box("border-amber-500/40",
      <Loader2 className="w-5 h-5 text-amber-400" />,
      "Payment received — confirming your order",
      "Your payment went through. We're still finalising the order (this occasionally takes a few extra minutes with Klarna). You'll receive a confirmation email as soon as it's ready — no further action needed.");
  }
  return null;
}
